import { t } from "@lingui/macro";
import {
  ArticleIcon,
  BriefcaseIcon,
  CheckCircleIcon,
  CircleNotchIcon,
  GlobeIcon,
  GraduationCapIcon,
  LifebuoyIcon,
  MedalIcon,
  SparkleIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";
import { Button } from "@reactive-resume/ui";
import type { LinkedInProfileDraft } from "@reactive-resume/dto";
import { draftSectionSummary } from "@reactive-resume/dto";
import { cn } from "@reactive-resume/utils";
import type { ReactNode } from "react";

type Props = {
  draft: LinkedInProfileDraft;
  pending: boolean;
  onConfirm: () => void;
  onRetry: () => void;
};

type SectionCardProps = {
  icon: ReactNode;
  title: string;
  count: number;
  children: ReactNode;
};

const formatPeriod = (start?: string, end?: string) => {
  if (!start && !end) {
    return "";
  }

  return [start ?? "", end ?? "Aujourd’hui"].filter(Boolean).join(" – ");
};

const SummaryChip = ({ label, count }: { label: string; count: number }) => (
  <span
    className={cn(
      "inline-flex min-h-7 items-center gap-1.5 rounded-full border px-2.5 text-[10px] font-bold",
      count > 0
        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
        : "border-slate-200 bg-slate-50 text-slate-400",
    )}
  >
    {count > 0 ? (
      <CheckCircleIcon aria-hidden size={13} weight="fill" />
    ) : (
      <WarningCircleIcon aria-hidden size={13} />
    )}

    {label}

    <span className="tabular-nums opacity-70">{count}</span>
  </span>
);

const EmptySection = ({ children }: { children: ReactNode }) => (
  <p className="flex items-start gap-2 text-[10px] leading-4 text-slate-500 sm:text-xs">
    <WarningCircleIcon aria-hidden size={14} className="mt-0.5 shrink-0 text-slate-400" />

    {children}
  </p>
);

const SectionCard = ({ icon, title, count, children }: SectionCardProps) => (
  <article
    className={cn(
      "rounded-2xl border bg-white p-4",
      count > 0 ? "border-indigo-100" : "border-slate-200 opacity-75",
    )}
  >
    <header className="mb-3 flex items-center gap-2">
      <span
        className={cn(
          "grid size-8 shrink-0 place-items-center rounded-xl",
          count > 0 ? "bg-indigo-50 text-indigo-600" : "bg-slate-50 text-slate-400",
        )}
      >
        {icon}
      </span>

      <h3 className="min-w-0 flex-1 text-xs font-bold text-[#071451] sm:text-sm">{title}</h3>

      <span
        className={cn(
          "grid min-w-7 place-items-center rounded-full px-2 py-1 text-[9px] font-bold tabular-nums",
          count > 0 ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400",
        )}
      >
        {count}
      </span>
    </header>

    {children}
  </article>
);

export const LinkedInPreviewStep = ({ draft, pending, onConfirm, onRetry }: Props) => {
  const summary = draftSectionSummary(draft);

  const total =
    summary.experiences +
    summary.educations +
    summary.certifications +
    summary.skills +
    summary.languages;

  return (
    <section className="mx-auto w-full max-w-3xl py-3 sm:py-6">
      <header>
        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-600">
          Vérification
        </p>

        <h1 className="mt-1 text-2xl font-bold tracking-tight text-[#071451] sm:text-3xl">
          Vérifiez les informations détectées
        </h1>

        <p className="mt-2 max-w-2xl text-xs leading-5 text-slate-600 sm:text-sm">
          Nous avons organisé les informations publiques de votre profil. Vous pourrez tout modifier
          ensuite dans le builder.
        </p>
      </header>

      <div className="mt-5 rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50/80 to-emerald-50/50 p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <span className="grid size-10 shrink-0 place-items-center rounded-full bg-emerald-500 text-white">
            <CheckCircleIcon aria-hidden size={22} weight="fill" />
          </span>

          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-wide text-emerald-600">
              Profil récupéré
            </p>

            <h2 className="mt-1 break-words text-base font-bold text-[#071451] sm:text-lg">
              {draft.name ?? "Nom non trouvé"}
            </h2>

            {draft.headline && (
              <p className="mt-1 text-xs font-medium leading-5 text-indigo-800 sm:text-sm">
                {draft.headline}
              </p>
            )}

            {draft.location && (
              <p className="mt-1 text-[10px] text-slate-500 sm:text-xs">{draft.location}</p>
            )}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-1.5">
          <SummaryChip label="Identité" count={summary.name ? 1 : 0} />

          <SummaryChip label="Expériences" count={summary.experiences} />

          <SummaryChip label="Formations" count={summary.educations} />

          <SummaryChip label="Certifications" count={summary.certifications} />

          <SummaryChip label="Compétences" count={summary.skills} />

          <SummaryChip label="Langues" count={summary.languages} />
        </div>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <SectionCard
          icon={<BriefcaseIcon size={17} />}
          title="Expériences"
          count={summary.experiences}
        >
          {draft.experiences.length === 0 ? (
            <EmptySection>
              Aucune expérience récupérée. Vous pourrez les ajouter dans le builder.
            </EmptySection>
          ) : (
            <ul className="space-y-3">
              {draft.experiences.slice(0, 4).map((item, index) => (
                <li
                  key={`${item.title}-${item.company}-${index}`}
                  className="border-l-2 border-indigo-100 pl-3"
                >
                  <p className="text-[11px] font-bold leading-4 text-[#071451] sm:text-xs">
                    {[item.title, item.company].filter(Boolean).join(" · ") ||
                      "Poste non renseigné"}
                  </p>

                  {(formatPeriod(item.start, item.end) || item.location) && (
                    <p className="mt-1 text-[9px] leading-4 text-slate-500 sm:text-[10px]">
                      {[formatPeriod(item.start, item.end), item.location]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  )}
                </li>
              ))}

              {draft.experiences.length > 4 && (
                <li className="text-[10px] font-bold text-indigo-600">
                  +{draft.experiences.length - 4} autre(s)
                </li>
              )}
            </ul>
          )}
        </SectionCard>

        <SectionCard
          icon={<GraduationCapIcon size={17} />}
          title="Formations"
          count={summary.educations}
        >
          {draft.educations.length === 0 ? (
            <EmptySection>Aucune formation récupérée.</EmptySection>
          ) : (
            <ul className="space-y-3">
              {draft.educations.slice(0, 4).map((item, index) => (
                <li
                  key={`${item.degree}-${item.school}-${index}`}
                  className="border-l-2 border-indigo-100 pl-3"
                >
                  <p className="text-[11px] font-bold leading-4 text-[#071451] sm:text-xs">
                    {[item.degree, item.school].filter(Boolean).join(" · ") || "Formation"}
                  </p>

                  {formatPeriod(item.start, item.end) && (
                    <p className="mt-1 text-[9px] text-slate-500 sm:text-[10px]">
                      {formatPeriod(item.start, item.end)}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        <SectionCard
          icon={<MedalIcon size={17} />}
          title="Certifications"
          count={summary.certifications}
        >
          {draft.certifications.length === 0 ? (
            <EmptySection>Aucune certification récupérée.</EmptySection>
          ) : (
            <ul className="space-y-2.5">
              {draft.certifications.slice(0, 5).map((item, index) => (
                <li key={`${item.name}-${index}`}>
                  <p className="text-[11px] font-bold leading-4 text-[#071451] sm:text-xs">
                    {item.name || "Certification"}
                  </p>

                  {(item.issuer || item.date) && (
                    <p className="mt-0.5 text-[9px] text-slate-500 sm:text-[10px]">
                      {[item.issuer, item.date].filter(Boolean).join(" · ")}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        <SectionCard icon={<SparkleIcon size={17} />} title="Compétences" count={summary.skills}>
          {draft.skills.length === 0 ? (
            <EmptySection>Aucune compétence récupérée.</EmptySection>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {draft.skills.slice(0, 12).map((skill, index) => (
                <span
                  key={`${skill}-${index}`}
                  className="rounded-lg border border-indigo-100 bg-indigo-50/60 px-2 py-1 text-[9px] font-semibold text-indigo-700 sm:text-[10px]"
                >
                  {skill}
                </span>
              ))}
            </div>
          )}
        </SectionCard>

        <SectionCard icon={<GlobeIcon size={17} />} title="Langues" count={summary.languages}>
          {draft.languages.length === 0 ? (
            <EmptySection>Aucune langue récupérée.</EmptySection>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {draft.languages.map((language, index) => (
                <span
                  key={`${language}-${index}`}
                  className="rounded-lg border border-emerald-100 bg-emerald-50 px-2 py-1 text-[9px] font-semibold text-emerald-700 sm:text-[10px]"
                >
                  {language}
                </span>
              ))}
            </div>
          )}
        </SectionCard>

        <SectionCard icon={<ArticleIcon size={17} />} title="À propos" count={draft.about ? 1 : 0}>
          {draft.about ? (
            <p className="line-clamp-6 whitespace-pre-line text-[10px] leading-4 text-slate-600 sm:text-xs sm:leading-5">
              {draft.about}
            </p>
          ) : (
            <EmptySection>Aucun résumé professionnel récupéré.</EmptySection>
          )}
        </SectionCard>
      </div>

      {total === 0 && (
        <div
          role="alert"
          className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs leading-5 text-amber-700"
        >
          <WarningCircleIcon aria-hidden size={16} weight="fill" className="mt-0.5 shrink-0" />
          {t`Aucune donnée exploitable n’a été récupérée. Vous pouvez réessayer ou utiliser l’import PDF (.pdf), Word (.docx) ou image (.jpg, .jpeg, .png, .webp).`}
        </div>
      )}

      <p className="mt-4 flex items-start gap-2 text-[10px] leading-4 text-slate-500 sm:text-xs">
        <LifebuoyIcon aria-hidden size={15} className="mt-0.5 shrink-0" />
        Vérifiez les informations : LinkedIn peut contenir des données anciennes ou incomplètes.
        Vous pourrez les corriger dans le builder.
      </p>

      <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row">
        <Button
          type="button"
          variant="outline"
          disabled={pending}
          onClick={onRetry}
          className="min-h-11 flex-1 rounded-xl"
        >
          Réessayer
        </Button>

        <Button
          type="button"
          disabled={pending || total === 0}
          onClick={onConfirm}
          className="min-h-11 flex-1 rounded-xl bg-gradient-to-r from-indigo-700 via-blue-600 to-emerald-500 font-bold text-white"
        >
          {pending ? (
            <CircleNotchIcon aria-hidden size={17} className="animate-spin" />
          ) : (
            <SparkleIcon aria-hidden size={17} weight="fill" />
          )}

          {pending ? "Création du CV…" : "Créer mon CV"}
        </Button>
      </div>
    </section>
  );
};
